'use client';

import type { ProductInformation, RecentProduct } from '@dealerportal/common';
import { createContext, useContext } from 'react';

/**
 * The few dealer tokens client components read. Only these are sent to the
 * browser; the full token list stays on the server.
 */
export interface ClientSiteTokens {
  /** `ff-viz-api-token`, the legacy visualizer key fallback */
  visualizerApiToken?: string;
  /** `companyid` */
  companyId?: string;
  /** `companyname` */
  companyName?: string;
  /** `PrimaryMarket` */
  primaryMarket?: string;
  /** `address` */
  address?: string;
  /** `all-showrooms`, a PDP SEO city fallback */
  allShowrooms?: string;
  /** `city1`, a PDP SEO city fallback */
  city1?: string;
}

export interface ClientContextProps {
  siteTokens: ClientSiteTokens;
  universalRetailerId: string | undefined;
  clientId: number | undefined;
  recaptcha: {
    Enabled: boolean;
    SiteKey?: string;
  };
  clientName: string | undefined;
  isMobileDevice: boolean;
  recentProducts: RecentProduct[];
  addRecentProduct: (product: ProductInformation) => void;
  disableOrderSample: boolean;
  useCiC: boolean;
  visualizerSettings?: {
    showVisualizer: boolean;
    apiKey?: string;
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const ClientContext = createContext<ClientContextProps>({
  siteTokens: {},
  universalRetailerId: undefined,
  clientId: undefined,
  recaptcha: { Enabled: false },
  isMobileDevice: false,
  clientName: undefined,
  recentProducts: [],
  addRecentProduct: () => {},
  disableOrderSample: false,
  useCiC: false,
});

/* eslint-disable @typescript-eslint/no-explicit-any */
export function useClientContext(): ClientContextProps {
  const ctx = useContext(ClientContext);

  return ctx;
}
